import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBestSellingProductsRequest } from "services/modules/dashboard/index";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  useTheme,
  Pagination,
  Avatar,
  Chip,
} from "@mui/material";
import BasicDatePicker from "components/Atoms/Input/DatePicker/DatePicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "components/Atoms/SharedComponents/Breadcrumbs/Breadcrumb";

const BestSellingProducts = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { bestSellingProducts, isLoading } = useSelector(
    (state) => state.dashboard,
  );

  const [selectedPeriod, setSelectedPeriod] = useState("this_month");
  const [page, setPage] = useState(1);
  const [customDateRange, setCustomDateRange] = useState({
    start_date: dayjs().startOf("month"),
    end_date: dayjs(),
  });

  const periods = [
    {
      id: "custom",
      label: t("dashboard.custom_range"),
      icon: <CalendarTodayIcon fontSize="small" />,
    },
    { id: "past_year", label: t("dashboard.last_year") },
    { id: "this_year", label: t("dashboard.this_year") },
    { id: "this_month", label: t("dashboard.this_month") },
  ];

  useEffect(() => {
    const params = {
      date_range_type: selectedPeriod,
      page_number: page,
      page_size: 20,
    };
    if (selectedPeriod === "custom") {
      params.start_date = customDateRange.start_date
        ? customDateRange.start_date.format("YYYY-MM-DD")
        : null;
      params.end_date = customDateRange.end_date
        ? customDateRange.end_date.format("YYYY-MM-DD")
        : null;
    }

    dispatch(getBestSellingProductsRequest({ params }));
  }, [dispatch, selectedPeriod, customDateRange, page]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const products = bestSellingProducts?.data?.products || [];
  const totalPages = bestSellingProducts?.extra?.total_pages || 1;

  return (
    <Box sx={{ p: 3 }}>
      <Loader open={isLoading?.loading} />

      {/* Breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumb
          data={[
            { active: true, path: "/dashboard", title: t("common.home") },
            { active: false, title: t("dashboard.best_selling_products") },
          ]}
        />
      </Box>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {t("dashboard.best_selling_products")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {periods.map((period) => (
            <Button
              key={period.id}
              variant={selectedPeriod === period.id ? "contained" : "outlined"}
              color="secondary"
              onClick={() => {
                setSelectedPeriod(period.id);
                setPage(1);
              }}
              startIcon={period.icon}
              sx={{
                borderRadius: "12px",
                borderColor: theme.palette.divider,
                height: 44,
                padding: "12px 16px",
                backgroundColor:
                  selectedPeriod === period.id ? "#F3E5F5" : "transparent",
                color:
                  selectedPeriod === period.id
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                border:
                  selectedPeriod === period.id
                    ? `1px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor:
                    selectedPeriod === period.id ? "#E1BEE7" : "#f5f5f5",
                  boxShadow: "none",
                  border:
                    selectedPeriod === period.id
                      ? `1px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                },
              }}
            >
              {period.label}
            </Button>
          ))}
        </Box>
      </Box>

      {selectedPeriod === "custom" && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            justifyContent: { xs: "center", sm: "flex-end" },
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <BasicDatePicker
            label={t("dashboard.start_date")}
            value={customDateRange.start_date}
            onChange={(newValue) => {
              setCustomDateRange((prev) => ({ ...prev, start_date: newValue }));
              setPage(1);
            }}
            maxDate={customDateRange.end_date}
            filter={false}
          />
          <BasicDatePicker
            label={t("dashboard.end_date")}
            value={customDateRange.end_date}
            onChange={(newValue) => {
              setCustomDateRange((prev) => ({ ...prev, end_date: newValue }));
              setPage(1);
            }}
            minDate={customDateRange.start_date}
            filter={false}
          />
        </Box>
      )}

      {/* Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "16px",
          boxShadow: "0px 2px 10px rgba(0,0,0,0.05)",
          mb: 3,
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="best selling products table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("products_option_values.product")}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                {t("dashboard.sales_qty")}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                {t("dashboard.orders_count")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.length > 0 ? (
              products.map((row, index) => (
                <TableRow
                  key={row.product_id || index}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "#fafafa" },
                  }}
                >
                  {/* Product cell: thumbnail + name */}
                  <TableCell component="th" scope="row">
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        src={row.thumbnail}
                        alt={row.name}
                        variant="rounded"
                        sx={{ width: 48, height: 48, borderRadius: "8px" }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          cursor: "pointer",
                          color: theme.palette.primary.main,
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={() =>
                          navigate(`/products/${row.product_id}?type=details`)
                        }
                      >
                        {row.name || "منتج غير معروف"}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* total_quantity_sold */}
                  <TableCell align="center">
                    <Chip
                      label={row.total_quantity_sold ?? 0}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        backgroundColor: `${theme.palette.primary.main}18`,
                        color: theme.palette.primary.main,
                        fontSize: "0.85rem",
                        px: 1,
                      }}
                    />
                  </TableCell>

                  {/* orders_count */}
                  <TableCell align="center">
                    <Chip
                      label={row.orders_count ?? 0}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        backgroundColor: "#E3F2FD",
                        color: "#1565C0",
                        fontSize: "0.85rem",
                        px: 1,
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={3}
                  align="center"
                  sx={{ py: 6, color: "text.secondary" }}
                >
                  {t("vendors.no_result")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
};

export default BestSellingProducts;
